import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
    Customer,
    GameType,
    LineNumbers,
    LinesManageButtonSignal,
    Lottery,
    MultiDrawTicketPacket,
    PickedNumber,
    Price,
    SelectableDrawDateTimes,
} from '../Types/Types';

import { numberArray } from 'utils/helpers';
import { calculateGrossValue } from 'utils/tax';

const initialState = {
    lottery: null as Lottery | null,
    lines: [] as Array<LineNumbers>,
    pickedNumbers: [] as Array<PickedNumber>,
    currencyCode: process.env.MIX_CURRENCY,
    price: null as Price | null,
    ticketPrice: 0,
    ticketPriceTax: 0,
    linePriceGross: 1,
    discountValue: null as number | null,
    gameType: null as GameType | null,
    gameTypes: [] as Array<GameType>,
    displayDrawCounter: false,
    resellerOptionsEnabled: false,
    resellerCustomers: [] as Array<Customer>,
    multiDrawTicketPackets: [] as Array<MultiDrawTicketPacket>,
    nextDrawNumber: 0,
    stakeMin: 1,
    stakeMax: 1,
    stakes: [] as Array<number>,
    stakeIsValid: true,
    numberOfDraws: 1,
    multiplier: 1,
    nextDraw: null as string | null,
    nextNextDraw: null as string | null,
    isMobile: true,
    linesManageButtonSignal: { value: 0 } as { value: LinesManageButtonSignal },
    selectableDrawDateTimes: [] as SelectableDrawDateTimes,
    permsPickerLines: [] as Array<Array<number>>,
    purchaseButtonDisabled: true,
    ticketPriceMin: null as number | null,
    ticketPriceMax: null as number | null,
};

export const storeSlice = createSlice({
    name: 'playStore',
    initialState,
    reducers: {
        setLottery: (state, action: PayloadAction<Lottery>) => {
            state.lottery = action.payload;
        },
        setPickedNumbers: (state, action: PayloadAction<PickedNumber[]>) => {
            state.pickedNumbers = action.payload;
        },
        setCurrencyCode: (state, action: PayloadAction<string>) => {
            state.currencyCode = action.payload;
        },
        setPrice: (state, action: PayloadAction<Price>) => {
            const price = action.payload;
            state.price = price;
            state.currencyCode = price.sellAmountCurrencyCode;
            state.linePriceGross = calculateGrossValue(price.priceNet, price.saleTaxPercent, price.saleTaxFixed);
        },
        setTicketPrice: (state, action: PayloadAction<number>) => {
            state.ticketPrice = action.payload;
        },
        setTicketPriceTax: (state, action: PayloadAction<number>) => {
            state.ticketPriceTax = action.payload;
        },
        setDiscountValue: (state, action: PayloadAction<number>) => {
            state.discountValue = action.payload;
        },
        setLinePriceGross: (state, action: PayloadAction<number>) => {
            state.linePriceGross = action.payload;
        },
        setGameType: (state, action: PayloadAction<GameType>) => {
            state.gameType = action.payload;
        },
        setGameTypes: (state, action: PayloadAction<GameType[]>) => {
            state.gameTypes = action.payload;
        },
        setDisplayDrawCounter: (state, action: PayloadAction<boolean>) => {
            state.displayDrawCounter = action.payload;
        },
        addLine: (state, action: PayloadAction<LineNumbers>) => {
            state.lines = [...state.lines, action.payload];
            state.stakes = [...state.stakes, Math.max(1.0, state.stakeMin)];
        },
        removeLine: (state, action: PayloadAction<number>) => {
            state.lines = state.lines.filter((line, index) => index !== action.payload);
        },
        removeStake: (state, action: PayloadAction<number>) => {
            state.stakes = state.stakes.filter((stake, index) => index !== action.payload);
        },
        setLine: (state, action: PayloadAction<{ lineIndex: number; lineNumbers: LineNumbers }>) => {
            state.lines = state.lines.map((numbers, index) =>
                index === action.payload.lineIndex ? action.payload.lineNumbers : numbers
            );
        },
        setLineStake: (state, action: PayloadAction<{ lineIndex: number; lineStake: number }>) => {
            state.stakes = state.stakes.map((stake, index) =>
                index === action.payload.lineIndex ? action.payload.lineStake : stake
            );
        },
        setLines: (state, action: PayloadAction<LineNumbers[]>) => {
            state.lines = action.payload;
            state.stakes = action.payload.map((_, index) => {
                if (state.stakes[index] !== undefined) {
                    return state.stakes[index];
                }
                return Math.max(1.0, state.stakeMin);
            });
        },
        setResellerOptionsEnabled: (state, action: PayloadAction<boolean>) => {
            state.resellerOptionsEnabled = action.payload;
        },
        setResellerCustomers: (state, action: PayloadAction<Customer[]>) => {
            state.resellerCustomers = action.payload;
        },
        setMultiDrawTicketPackets: (state, action: PayloadAction<MultiDrawTicketPacket[]>) => {
            state.multiDrawTicketPackets = action.payload;
        },
        setNextDrawNumber: (state, action: PayloadAction<number>) => {
            state.nextDrawNumber = action.payload;
        },
        setStakeMin: (state, action: PayloadAction<number>) => {
            state.stakeMin = action.payload;
        },
        setStakeMax: (state, action: PayloadAction<number>) => {
            state.stakeMax = action.payload;
        },
        setStakes: (state, action: PayloadAction<number[]>) => {
            state.stakes = action.payload;
        },
        setStakeIsValid: (state, action: PayloadAction<boolean>) => {
            state.stakeIsValid = action.payload;
        },
        setDefaultStakes: (state, action: PayloadAction<number>) => {
            state.stakes = numberArray(action.payload).map(() => Math.max(1.0, state.stakeMin));
        },
        setNumberOfDraws: (state, action: PayloadAction<number>) => {
            state.numberOfDraws = action.payload;
        },
        setMultiplier: (state, action: PayloadAction<number>) => {
            state.multiplier = action.payload;
        },
        setNextDraw: (state, action: PayloadAction<string>) => {
            state.nextDraw = action.payload;
        },
        setNextNextDraw: (state, action: PayloadAction<string>) => {
            state.nextNextDraw = action.payload;
        },
        setIsMobile: (state, action: PayloadAction<boolean>) => {
            state.isMobile = action.payload;
        },
        clickLinesManageButton: (state, action: PayloadAction<LinesManageButtonSignal>) => {
            state.linesManageButtonSignal = { value: action.payload };
        },
        setSelectableDrawDateTimes: (state, action: PayloadAction<SelectableDrawDateTimes>) => {
            state.selectableDrawDateTimes = action.payload;
        },
        setPermsPickerLines: (state, action: PayloadAction<number[][]>) => {
            state.permsPickerLines = action.payload;
        },
        setPurchaseButtonDisabled: (state, action: PayloadAction<boolean>) => {
            state.purchaseButtonDisabled = action.payload;
        },
        setTicketPriceMin: (state, action: PayloadAction<number | null>) => {
            state.ticketPriceMin = action.payload;
        },
        setTicketPriceMax: (state, action: PayloadAction<number | null>) => {
            state.ticketPriceMax = action.payload;
        },
    },
});
export const {
    setLottery,
    setPickedNumbers,
    setCurrencyCode,
    setPrice,
    setTicketPrice,
    setTicketPriceTax,
    setDiscountValue,
    setLinePriceGross,
    setGameType,
    setGameTypes,
    setDisplayDrawCounter,
    addLine,
    removeLine,
    removeStake,
    setLine,
    setLineStake,
    setLines,
    setResellerOptionsEnabled,
    setResellerCustomers,
    setMultiDrawTicketPackets,
    setNextDrawNumber,
    setStakeMin,
    setStakeMax,
    setStakes,
    setStakeIsValid,
    setDefaultStakes,
    setNumberOfDraws,
    setMultiplier,
    setNextDraw,
    setNextNextDraw,
    setIsMobile,
    clickLinesManageButton,
    setSelectableDrawDateTimes,
    setPermsPickerLines,
    setPurchaseButtonDisabled,
    setTicketPriceMax,
    setTicketPriceMin,
} = storeSlice.actions;
